import React, { useMemo } from "react";
import { fmtDateGB } from "../lib/format";

/**
 * Self-contained Swiss-style Gantt chart.
 * Props:
 *   tasks: [{id, name, start_date, end_date, status, dependencies[], milestone, progress_pct}]
 *   onTaskClick(task)
 */
const DAY_MS = 24 * 60 * 60 * 1000;

function parseISO(d) {
  if (!d) return null;
  const [y, m, day] = d.split("-").map(Number);
  return new Date(Date.UTC(y, (m || 1) - 1, day || 1));
}

export default function GanttChart({ tasks = [], onTaskClick }) {
  const meta = useMemo(() => {
    if (!tasks.length) return null;
    const starts = tasks.map(t => parseISO(t.start_date)).filter(Boolean);
    const ends = tasks.map(t => parseISO(t.end_date)).filter(Boolean);
    if (!starts.length) return null;
    const min = new Date(Math.min(...starts.map(d => d.getTime())));
    const max = new Date(Math.max(...ends.map(d => d.getTime())));
    const pad = 1;
    const start = new Date(min.getTime() - pad * DAY_MS);
    const end = new Date(max.getTime() + pad * DAY_MS);
    const totalDays = Math.max(1, Math.ceil((end - start) / DAY_MS));
    return { start, end, totalDays };
  }, [tasks]);

  if (!meta) {
    return (
      <div className="swiss-card-flat p-10 text-center muted" data-testid="gantt-empty">
        No tasks yet. Seed the default UK installation timeline below.
      </div>
    );
  }

  const dayW = 24; // px
  const trackWidth = meta.totalDays * dayW;

  const idToPos = {};
  tasks.forEach(t => {
    const s = parseISO(t.start_date);
    const e = parseISO(t.end_date);
    if (!s || !e) return;
    const leftDays = Math.max(0, (s - meta.start) / DAY_MS);
    const widthDays = Math.max(1, (e - s) / DAY_MS + 1);
    idToPos[t.id] = { left: leftDays * dayW, width: widthDays * dayW };
  });

  // Month tick markers
  const months = [];
  const cursor = new Date(meta.start);
  cursor.setUTCDate(1);
  while (cursor <= meta.end) {
    const days = (cursor - meta.start) / DAY_MS;
    months.push({
      label: cursor.toLocaleDateString("en-GB", { month: "short", year: "2-digit" }),
      left: days * dayW,
    });
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }

  // Today line
  const today = new Date();
  const todayOffset = Math.max(0, (today - meta.start) / DAY_MS) * dayW;
  const todayInRange = today >= meta.start && today <= meta.end;

  return (
    <div className="swiss-card-flat scrollx" data-testid="gantt-chart">
      <div style={{ minWidth: 280 + trackWidth }}>
        {/* Header */}
        <div className="gantt-row" style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
          <div className="gantt-task-label label">Task</div>
          <div className="relative" style={{ width: trackWidth, height: 38 }}>
            {months.map((m, i) => (
              <div
                key={i}
                className="absolute top-0 h-full border-l border-[var(--line)] px-1 text-[10px] font-mono muted uppercase tracking-wider"
                style={{ left: m.left }}
              >
                {m.label}
              </div>
            ))}
          </div>
        </div>

        {/* Rows */}
        {tasks.map((t) => {
          const pos = idToPos[t.id];
          if (!pos) return null;
          return (
            <div className="gantt-row" key={t.id} data-testid={`gantt-row-${t.id}`}>
              <div className="gantt-task-label flex items-center justify-between">
                <span className="truncate pr-2">{t.milestone ? "◆ " : ""}{t.name}</span>
                <span className="label !text-[9px]">{t.progress_pct}%</span>
              </div>
              <div className="gantt-track relative" style={{ width: trackWidth }}>
                {todayInRange && (
                  <div
                    className="absolute top-0 bottom-0 border-l-2 border-dashed border-[var(--signal)] opacity-70 pointer-events-none"
                    style={{ left: todayOffset }}
                  />
                )}
                <div
                  className={`gantt-bar ${t.milestone ? "milestone" : t.status}`}
                  style={{ left: pos.left, width: t.milestone ? 22 : pos.width }}
                  onClick={() => onTaskClick && onTaskClick(t)}
                  data-testid={`gantt-bar-${t.id}`}
                  title={`${t.name} · ${fmtDateGB(t.start_date)} → ${fmtDateGB(t.end_date)}`}
                >
                  {!t.milestone && <span className="truncate">{t.name}</span>}
                </div>
                {/* Dependency lines: simple horizontal dotted indicator */}
                {t.dependencies?.map(depId => {
                  const dep = idToPos[depId];
                  if (!dep) return null;
                  const fromX = dep.left + dep.width;
                  const toX = pos.left;
                  if (toX <= fromX) return null;
                  return (
                    <div
                      key={depId}
                      className="absolute top-1/2 border-t border-dashed border-[var(--ink-3)]"
                      style={{ left: fromX, width: toX - fromX }}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
